<template>
  <div class="review-page">
    <header class="review-page__header">
      <button class="back-btn" @click="goBack">← Dashboard</button>
      <div class="header__title">
        <span class="header__icon">🧑‍💼</span>
        <div>
          <span class="header__name">Fachbereich – Antragsprüfung</span>
          <span class="header__sub">Baumschutz – Ausnahmegenehmigung</span>
        </div>
      </div>
      <div class="header__badge">FormIQ 3 · Sachbearbeitung</div>
    </header>

    <div class="review-page__body">
      <aside class="case-list">
        <div class="case-list__header">Abgeschlossene Anträge</div>
        <p v-if="loadError" class="case-list__error">⚠ {{ loadError }}</p>
        <p v-else-if="!cases.length" class="case-list__empty">Keine abgeschlossenen Anträge.</p>
        <div
          v-for="c in cases"
          :key="c.id"
          class="case-list__item"
          :class="{ 'case-list__item--active': selectedCase && selectedCase.id === c.id }"
          @click="selectCase(c)"
        >
          <span class="case-list__id">{{ c.id }}</span>
          <span
            v-if="c.documents && c.documents.some((d) => !d.approved)"
            class="badge badge--review"
          >
            Aktion erforderlich
          </span>
        </div>
      </aside>

      <main class="case-detail" v-if="selectedCase">
        <h2 class="case-detail__title">Antrag {{ selectedCase.id }}</h2>

         <section class="case-detail__data" v-if="selectedCase.data && selectedCase.data.applicant">
          <h3 class="section-title">Antragsteller</h3>
          <div class="data-card">
            <div class="applicant-name">
              {{ selectedCase.data.applicant.vorname }} {{ selectedCase.data.applicant.nachname }}
            </div>
            <dl class="data-grid">
              <div class="data-grid__row">
                <dt>Geburtsdatum</dt>
                <dd>{{ selectedCase.data.applicant.geburtsdatum || '–' }}</dd>
              </div>
              <div class="data-grid__row">
                <dt>Geburtsort</dt>
                <dd>{{ selectedCase.data.applicant.geburtsort || '–' }}</dd>
              </div>
              <div class="data-grid__row">
                <dt>E-Mail</dt>
                <dd>{{ selectedCase.data.applicant.email || '–' }}</dd>
              </div>
              <div class="data-grid__row">
                <dt>Mobil</dt>
                <dd>{{ selectedCase.data.applicant.mobil || '–' }}</dd>
              </div>
              <div class="data-grid__row">
                <dt>Rechtliche Hinweise bestätigt</dt>
                <dd>{{ selectedCase.data.applicant.confirm_rechtliche_hinweise ? 'Ja' : 'Nein' }}</dd>
              </div>
            </dl>
          </div>
        </section>

        <section class="case-detail__documents">
          <h3 class="section-title">Dokumente</h3>
          <div v-for="doc in selectedCase.documents" :key="doc.document_id" class="doc-card">
            <div class="doc-card__header">
              <strong>{{ categoryLabel(doc.document_type) }}</strong>
              <span v-if="doc.approved" class="badge badge--ok">✓ Geprüft</span>
              <span v-else-if="categoryConflict(doc)" class="badge badge--conflict">⚠ Kategorie weicht ab</span>
              <span v-else class="badge badge--review">Aktion erforderlich</span>
            </div>
            <div class="doc-card__row">
              <span>Dateiname:</span> {{ doc.document_name || '–' }}
              <button
                v-if="doc.document_name"
                class="doc-card__link"
                :disabled="previewLoading === doc.document_id"
                @click="openPreview(doc)"
              >
                {{ previewLoading === doc.document_id ? 'Lädt…' : 'Vorschau' }}
              </button>
            </div>
            <div class="doc-card__row"><span>Modellvorschlag:</span> {{ categoryLabel(doc.category_model) }}</div>
            <div class="doc-card__row"><span>Angabe des Bürgers:</span> {{ categoryLabel(doc.category_user) }}</div>
            <div class="doc-card__row"><span>Finale Kategorie:</span> {{ categoryLabel(doc.category_caseworker) }}</div>
            <p v-if="categoryConflict(doc)" class="doc-card__conflict-note">
              ⚠ Modell und Bürger widersprechen sich: Modell schlägt
              <strong>{{ categoryLabel(doc.category_model) }}</strong> vor, der Bürger hat
              <strong>{{ categoryLabel(doc.category_user) }}</strong> angegeben. Bitte finale Kategorie prüfen.
            </p>

            <div class="doc-card__resolve">
              <input
                type="text"
                class="doc-card__input"
                placeholder="Finale Kategorie eingeben"
                v-model="resolveDrafts[doc.document_id]"
              />
              <button class="doc-card__btn" @click="resolve(doc)">Übernehmen</button>
              <button
                class="doc-card__btn doc-card__btn--approve"
                :disabled="doc.approved"
                @click="approve(doc)"
              >
                ✓ Als geprüft markieren
              </button>
              <button class="doc-card__btn doc-card__btn--secondary" @click="toggleHistory(doc)">
                Verlauf {{ expandedHistory === doc.document_id ? '▲' : '▼' }}
              </button>
            </div>

            <div v-if="expandedHistory === doc.document_id" class="doc-card__history">
              <p v-if="!history.length" class="doc-card__history-empty">Kein Verlauf vorhanden.</p>
              <div v-for="(entry, i) in history" :key="i" class="doc-card__history-entry">
                <span class="history__actor">{{ entry.actor === 'citizen' ? 'Bürger' : 'Sachbearbeiter' }}</span>
                <span class="history__category">{{ categoryLabel(entry.category) }}</span>
                <span class="history__timestamp">{{ formatTimestamp(entry.timestamp) }}</span>
              </div>
            </div>
          </div>
        </section>

        <section class="case-detail__data" v-if="selectedCase.data && selectedCase.data.document_extractions">
          <h3 class="section-title">Antragsdaten</h3>
          <div v-for="ext in selectedCase.data.document_extractions" :key="ext.document_type" class="data-card">
            <strong class="data-card__title">{{ categoryLabel(ext.document_type) }}</strong>
            <pre>{{ JSON.stringify(ext.extraction, null, 2) }}</pre>
          </div>
        </section>
      </main>

      <main class="case-detail case-detail--empty" v-else>
        Wählen Sie einen Antrag aus der Liste.
      </main>
    </div>

    <div v-if="previewDoc" class="preview-overlay" @click.self="closePreview">
      <div class="preview-modal">
        <div class="preview-modal__header">
          <span class="preview-modal__name">{{ previewDoc.document_name }}</span>
          <button class="preview-modal__close" @click="closePreview">✕</button>
        </div>
        <p v-if="previewError" class="preview-modal__error">⚠ {{ previewError }}</p>
        <iframe v-else-if="previewUrl" :src="previewUrl" class="preview-modal__frame" title="Dokumentvorschau" />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { flowAssignmentApi } from '../services/flowAssignmentApi.js';
import { caseApi } from '../services/caseApi.js';
import { DOCUMENT_CATEGORIES } from '../constants/documentCategories.js';

const FLOW_KEY = 'formiq3';
const DEFAULT_CLIENT_ID = 'form3_baumschutz';

export default {
  name: 'CaseworkerReviewPage',

  setup() {
    const router = useRouter();
    const resolvedClientId = ref(DEFAULT_CLIENT_ID);
    const cases = ref([]);
    const loadError = ref(null);
    const selectedCase = ref(null);
    const resolveDrafts = ref({});
    const expandedHistory = ref(null);
    const history = ref([]);
    const previewDoc = ref(null);
    const previewUrl = ref(null);
    const previewError = ref(null);
    const previewLoading = ref(null);

    const loadCases = async () => {
      loadError.value = null;
      try {
        const result = await caseApi.listCompleteCases(resolvedClientId.value);
        cases.value = result.documents || [];
      } catch (e) {
        loadError.value = e.message || 'Anträge konnten nicht geladen werden.';
      }
    };

    onMounted(async () => {
      try {
        const assignment = await flowAssignmentApi.getFlowAssignment(FLOW_KEY);
        if (assignment?.client_id) resolvedClientId.value = assignment.client_id;
      } catch {
        // keep the default client_id if the lookup fails
      }
      await loadCases();
    });

    const selectCase = (c) => {
      selectedCase.value = c;
      resolveDrafts.value = Object.fromEntries(
        (c.documents || []).map((doc) => [doc.document_id, doc.category_model || doc.category_user || ''])
      );
      expandedHistory.value = null;
      history.value = [];
    };

    const resolve = async (doc) => {
      const value = resolveDrafts.value[doc.document_id] || doc.category_user || doc.category_model;
      if (!value) return;
      try {
        const result = await caseApi.resolveDocumentCategory(
          resolvedClientId.value, selectedCase.value.id, doc.document_id, value
        );
        selectedCase.value = { ...selectedCase.value, documents: result.documents };
        const idx = cases.value.findIndex((c) => c.id === selectedCase.value.id);
        if (idx !== -1) cases.value[idx] = selectedCase.value;
      } catch (e) {
        loadError.value = e.message || 'Kategorie konnte nicht übernommen werden.';
      }
    };

    const approve = async (doc) => {
      try {
        const result = await caseApi.approveDocumentCategory(
          resolvedClientId.value, selectedCase.value.id, doc.document_id
        );
        selectedCase.value = { ...selectedCase.value, documents: result.documents };
        const idx = cases.value.findIndex((c) => c.id === selectedCase.value.id);
        if (idx !== -1) cases.value[idx] = selectedCase.value;
      } catch (e) {
        loadError.value = e.message || 'Dokument konnte nicht als geprüft markiert werden.';
      }
    };

    const toggleHistory = async (doc) => {
      if (expandedHistory.value === doc.document_id) {
        expandedHistory.value = null;
        return;
      }
      expandedHistory.value = doc.document_id;
      try {
        const result = await caseApi.getDocumentHistory(
          resolvedClientId.value, selectedCase.value.id, doc.document_id
        );
        history.value = result.history || [];
      } catch (e) {
        loadError.value = e.message || 'Verlauf konnte nicht geladen werden.';
      }
    };

    const goBack = () => router.push('/dashboard');

    const categoryLabel = (value) => {
      if (!value) return '–';
      const match = DOCUMENT_CATEGORIES.find(
        (c) => c.id.toLowerCase() === value.toLowerCase() || c.label.toLowerCase() === value.toLowerCase()
      );
      return match ? match.label : value;
    };

    // Model and citizen disagreeing on a document's category is exactly the
    // case the caseworker needs flagged - surfaced separately from the
    // generic needs_review badge so it's clear *why* review is required.
    const categoryConflict = (doc) => (
      !!doc.category_model && !!doc.category_user &&
      doc.category_model.trim().toLowerCase() !== doc.category_user.trim().toLowerCase()
    );

    const formatTimestamp = (timestamp) => {
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) return timestamp;
      return date.toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    };

    const openPreview = async (doc) => {
      previewDoc.value = doc;
      previewUrl.value = null;
      previewError.value = null;
      previewLoading.value = doc.document_id;
      try {
        const result = await caseApi.getDocumentPreviewUrl(
          resolvedClientId.value, selectedCase.value.id, doc.document_id
        );
        previewUrl.value = result.document_url;
      } catch (e) {
        previewError.value = e.message || 'Vorschau konnte nicht geladen werden.';
      } finally {
        previewLoading.value = null;
      }
    };

    const closePreview = () => {
      previewDoc.value = null;
      previewUrl.value = null;
      previewError.value = null;
    };

    return {
      cases, loadError, selectedCase, resolveDrafts, expandedHistory, history,
      previewDoc, previewUrl, previewError, previewLoading,
      selectCase, resolve, approve, toggleHistory, goBack, openPreview, closePreview, formatTimestamp,
      categoryLabel, categoryConflict,
    };
  },
};
</script>

<style scoped>
.review-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #eef0f4;
  overflow: hidden;
}

.review-page__header {
  background: #ffffff;
  border-bottom: 3px solid #c0121c;
  padding: 10px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.back-btn {
  background: #f1f5f9;
  border: 1px solid #d0d7de;
  color: #1e293b;
  padding: 7px 14px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.back-btn:hover { background: #e2e8f0; }

.header__title { display: flex; align-items: center; gap: 12px; flex: 1; }
.header__icon { font-size: 22px; }
.header__name { display: block; font-size: 15px; font-weight: 700; color: #1a1a1a; }
.header__sub { display: block; font-size: 11px; color: #64748b; margin-top: 2px; }
.header__badge {
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  color: #3730a3;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
}

.review-page__body {
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
}

.case-list {
  width: 260px;
  flex-shrink: 0;
  background: #f5f7fa;
  border-right: 1px solid #d0d7de;
  overflow-y: auto;
  padding: 12px;
}
.case-list__header {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.case-list__error, .case-list__empty { font-size: 12px; color: #6b7280; }
.case-list__item {
  padding: 10px 12px;
  border-radius: 8px;
  border-left: 3px solid transparent;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 4px;
  transition: background-color 0.12s ease;
}
.case-list__item:hover { background: #e2e8f0; }
.case-list__item--active { background: #dbeafe; border-left-color: #003366; }
.case-list__id {
  font-family: monospace;
  font-size: 11px;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
}

.case-detail {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}
.case-detail--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
}
.case-detail__title { font-size: 20px; font-weight: 800; color: #111827; margin-bottom: 20px; }

.section-title {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
}

.doc-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 18px;
  margin-bottom: 14px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}
.doc-card__header { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.doc-card__header strong { font-size: 15px; font-weight: 700; color: #111827; letter-spacing: 0.02em; }
.doc-card__row { display: flex; align-items: baseline; gap: 6px; font-size: 13px; color: #1f2937; margin-bottom: 6px; }
.doc-card__row span { color: #6b7280; flex-shrink: 0; }
.doc-card__link {
  margin-left: auto;
  background: none;
  border: none;
  color: #003366;
  font-size: 12px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}
.doc-card__link:disabled { color: #9ca3af; cursor: default; text-decoration: none; }

.badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
}
.badge--review { background: #fef3c7; color: #92400e; }
.badge--ok { background: #d1fae5; color: #065f46; }
.badge--conflict { background: #fee2e2; color: #991b1b; }

.doc-card__conflict-note {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #991b1b;
  font-size: 12px;
  padding: 8px 10px;
  margin: 8px 0 0;
}

.doc-card__resolve { display: flex; gap: 8px; margin-top: 10px; flex-wrap: wrap; }
.doc-card__input {
  flex: 1;
  min-width: 160px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 12px;
}
.doc-card__btn {
  background: #003366;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.doc-card__btn--secondary { background: #f1f5f9; color: #1e293b; }
.doc-card__btn--approve { background: #059669; }
.doc-card__btn--approve:disabled { background: #d1fae5; color: #6b7280; cursor: default; }

.doc-card__history {
  margin-top: 10px;
  padding: 10px;
  background: #f9fafb;
  border-radius: 8px;
}
.doc-card__history-empty { font-size: 12px; color: #9ca3af; }
.doc-card__history-entry {
  display: flex;
  gap: 10px;
  font-size: 12px;
  padding: 4px 0;
  border-bottom: 1px solid #e5e7eb;
}
.history__actor { font-weight: 700; color: #1e293b; }
.history__category { color: #374151; }
.history__timestamp { margin-left: auto; color: #9ca3af; }

.case-detail__data { margin-top: 28px; }
.data-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 10px;
  font-size: 12px;
}

.applicant-name { font-size: 15px; font-weight: 700; color: #111827; margin-bottom: 12px; }

.data-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 8px 24px; }
.data-grid__row { display: flex; flex-direction: column; gap: 2px; }
.data-grid__row dt {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.data-grid__row dd { font-size: 13px; color: #1f2937; }

.data-card__title { display: block; font-size: 13px; font-weight: 700; color: #111827; margin-bottom: 8px; }
.data-card pre {
  white-space: pre-wrap;
  word-break: break-word;
  color: #374151;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 10px;
  font-size: 11.5px;
  line-height: 1.5;
}

.preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}
.preview-modal {
  background: #fff;
  border-radius: 10px;
  width: min(900px, 90vw);
  height: min(85vh, 900px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0,0,0,0.25);
}
.preview-modal__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
}
.preview-modal__name { flex: 1; font-size: 14px; font-weight: 700; color: #1a1a2e; word-break: break-all; }
.preview-modal__close {
  background: none;
  border: none;
  font-size: 16px;
  color: #6b7280;
  cursor: pointer;
}
.preview-modal__error { padding: 24px; text-align: center; color: #dc2626; font-size: 13px; }
.preview-modal__frame { flex: 1; border: none; width: 100%; }
</style>
